import React, { useState, useRef, useEffect } from "react";
import { BsInfoCircle, BsCurrencyDollar, BsChevronDown, BsShieldCheck, BsLockFill } from "react-icons/bs";
import { FiMinus, FiPlus } from "react-icons/fi";
import { FaGift } from "react-icons/fa";
import {
  getSupportedChains,
  getChain,
  getCurrencyKeysByNetwork,
  currencyIcons,
} from "../../utils/currencies";

const membershipDurations = [
  { value: "lifetime", label: "Lifetime", recommended: true },
  { value: "yearly", label: "1 Year" },
  { value: "monthly", label: "Monthly" },
];

const Step3SaleSettings = ({ formik, isPublished }) => {
  const blockchain = formik.values.blockchain || "sepolia";
  const supportedChains = getSupportedChains();

  // Get available currencies for the selected chain
  const availableCurrencies = getCurrencyKeysByNetwork(blockchain);

  const currentSupply = formik.values.totalSupply || 10;
  const isFree = (formik.values.saleSettings?.price ?? 0) === 0;
  const selectedCurrency = formik.values.saleSettings?.currency || availableCurrencies[0] || "ETH";

  const [currencyOpen, setCurrencyOpen] = useState(false);
  const currencyRef = useRef(null);

  // Close currency dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (currencyRef.current && !currencyRef.current.contains(e.target)) {
        setCurrencyOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const handleBlockchainChange = (chain) => {
    formik.setFieldValue("blockchain", chain.network);
    // Auto-reset currency to first token available on the new chain
    const currencies = getCurrencyKeysByNetwork(chain.network);
    const currentCurrencyStillValid = currencies.includes(selectedCurrency);
    if (!currentCurrencyStillValid && currencies.length > 0) {
      formik.setFieldValue("saleSettings.currency", currencies[0]);
    }
  };

  const handleFreeToggle = () => {
    if (isFree) {
      formik.setFieldValue("saleSettings.price", 1);
    } else {
      formik.setFieldValue("saleSettings.price", 0);
    }
  };

  const handleSupplyChange = (increment) => {
    const newValue = currentSupply + increment;
    if (newValue >= 1 && newValue <= 1000) {
      formik.setFieldValue("totalSupply", newValue);
    }
  };

  const CurrencyIcon = currencyIcons[selectedCurrency];

  return (
    <div className="space-y-6 md:space-y-8">
      <div className="text-center mb-6 md:mb-8">
        <div className="inline-flex items-center justify-center w-14 h-14 md:w-16 md:h-16 rounded-2xl bg-gradient-to-br from-green-100 to-emerald-100 mb-3">
          <BsCurrencyDollar className="text-2xl md:text-3xl text-green-600" />
        </div>
        <h2 className="text-xl md:text-2xl font-bold text-gray-800 mb-2">
          Sale Settings
        </h2>
        <p className="text-sm md:text-base text-gray-600">
          Choose your blockchain, supply, and membership price
        </p>
      </div>

      {isPublished && (
        <div className="flex items-start gap-3 p-4 bg-amber-50 border border-amber-200 rounded-xl">
          <BsLockFill className="text-amber-500 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-amber-800">
            <span className="font-semibold">These settings are locked.</span> Blockchain, supply, price, and currency are set on-chain and cannot be changed after publishing. Membership duration can still be updated.
          </p>
        </div>
      )}

      {/* Total Supply */}
      <div className={`bg-gradient-to-br from-purple-50 via-blue-50 to-purple-50 rounded-xl md:rounded-2xl p-5 md:p-6 border border-purple-100 ${isPublished ? "opacity-60" : ""}`}>
        <label className="block text-sm font-medium text-gray-700 mb-4 text-center flex items-center justify-center gap-2">
          🎯 Total Supply of NFTs *
          {isPublished && <BsLockFill className="text-amber-400 text-xs" />}
        </label>
        <div className="flex items-center justify-center gap-4 md:gap-6">
          <button
            type="button"
            onClick={() => !isPublished && handleSupplyChange(-1)}
            disabled={currentSupply <= 1 || isPublished}
            className="w-10 h-10 md:w-12 md:h-12 rounded-full bg-white shadow-md flex items-center justify-center hover:bg-gray-50 hover:scale-110 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 transition-all"
          >
            <FiMinus className="text-lg md:text-xl" />
          </button>
          <div className="text-center">
            <input
              type="number"
              name="totalSupply"
              value={currentSupply}
              disabled={isPublished}
              onChange={(e) => {
                if (isPublished) return;
                const val = parseInt(e.target.value) || 1;
                formik.setFieldValue("totalSupply", Math.min(1000, Math.max(1, val)));
              }}
              className={`w-20 md:w-24 text-3xl md:text-4xl font-bold text-center bg-transparent border-b-2 border-purple-300 focus:border-purple-500 outline-none text-primary ${isPublished ? "cursor-not-allowed" : ""}`}
              min={1}
              max={1000}
            />
            <p className="text-xs md:text-sm text-gray-500 mt-1">NFTs in collection</p>
          </div>
          <button
            type="button"
            onClick={() => !isPublished && handleSupplyChange(1)}
            disabled={currentSupply >= 1000 || isPublished}
            className="w-10 h-10 md:w-12 md:h-12 rounded-full bg-white shadow-md flex items-center justify-center hover:bg-gray-50 hover:scale-110 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 transition-all"
          >
            <FiPlus className="text-lg md:text-xl" />
          </button>
        </div>
        <div className="flex justify-center gap-2 mt-4">
          {[10, 25, 50, 100].map((num) => (
            <button
              key={num}
              type="button"
              onClick={() => !isPublished && formik.setFieldValue("totalSupply", num)}
              disabled={isPublished}
              className={`px-4 py-1.5 rounded-full text-sm transition-all ${
                currentSupply === num
                  ? "bg-primary text-white"
                  : "bg-white text-gray-600 hover:bg-gray-100"
              } ${isPublished ? "cursor-not-allowed" : ""}`}
            >
              {num}
            </button>
          ))}
        </div>
        {formik.touched.totalSupply && formik.errors.totalSupply && (
          <p className="mt-2 text-sm text-red-500 text-center">
            {formik.errors.totalSupply}
          </p>
        )}
        <p className="text-xs text-center text-gray-500 mt-3">
          {isPublished ? "🔒 Supply is locked after publishing" : "⚠️ Supply cannot be changed after publishing"}
        </p>
      </div>

      {/* Blockchain Network + Membership Price (side by side context) */}
      <div className="bg-white rounded-xl md:rounded-2xl p-5 md:p-6 border-2 border-gray-200 space-y-5">

        {/* Blockchain Picker */}
        <div className={isPublished ? "opacity-60" : ""}>
          <label className="block text-sm font-medium text-gray-700 mb-3 flex items-center gap-2">
            <BsShieldCheck className="text-primary" />
            Blockchain Network *
            {isPublished && <BsLockFill className="text-amber-400 text-xs" />}
          </label>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
            {supportedChains.map((chain) => {
              const chainInfo = getChain({ chainId: chain.id }) || chain;
              const isSelected = blockchain === chain.network;
              const isTestnet =
                chain.network.includes("test") || chain.network.includes("sepolia");

              return (
                <button
                  key={chain.id}
                  type="button"
                  onClick={() => !isPublished && handleBlockchainChange(chain)}
                  disabled={isPublished}
                  className={`p-2.5 rounded-xl border-2 flex items-center gap-2 transition-all text-left ${
                    isSelected
                      ? "border-primary bg-gradient-to-br from-purple-50 to-blue-50 shadow-sm"
                      : "border-gray-200 hover:border-gray-300 hover:bg-gray-50"
                  } ${isPublished ? "cursor-not-allowed" : ""}`}
                >
                  {chainInfo.icon ? (
                    <img
                      src={chainInfo.icon}
                      alt={chainInfo.name}
                      className="w-6 h-6 flex-shrink-0 rounded-full"
                    />
                  ) : (
                    <div className="w-6 h-6 bg-gray-200 rounded-full flex items-center justify-center flex-shrink-0">
                      <BsShieldCheck className="text-gray-500 text-xs" />
                    </div>
                  )}
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-gray-800 text-xs truncate">
                      {chain.shortName || chainInfo.name}
                    </p>
                    <p className={`text-xs ${isTestnet ? "text-orange-500" : "text-green-500"}`}>
                      {isTestnet ? "Testnet" : "Mainnet"}
                    </p>
                  </div>
                  {isSelected && (
                    <svg className="w-4 h-4 text-primary flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                  )}
                </button>
              );
            })}
          </div>
          {formik.touched.blockchain && formik.errors.blockchain && (
            <p className="mt-2 text-sm text-red-500">{formik.errors.blockchain}</p>
          )}
        </div>

        {/* Divider */}
        <div className="border-t border-gray-100" />

        {/* Membership Price */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            💰 Membership Price *
          </label>
          <p className="text-xs text-gray-500 mb-4">
            Each member pays this price to mint their NFT. Payment goes directly to your wallet.
          </p>

          {/* Free / Paid Toggle */}
          <div className={`flex items-center gap-3 mb-5 p-3 bg-gray-50 rounded-xl border border-gray-200 ${isPublished ? "opacity-60" : ""}`}>
            <button
              type="button"
              onClick={!isPublished ? handleFreeToggle : undefined}
              disabled={isPublished}
              className={`relative inline-flex h-6 w-11 flex-shrink-0 rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                isFree ? "bg-green-500" : "bg-gray-300"
              } ${isPublished ? "cursor-not-allowed" : "cursor-pointer"}`}
              aria-pressed={isFree}
            >
              <span
                className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                  isFree ? "translate-x-5" : "translate-x-0"
                }`}
              />
            </button>
            <div className="flex items-center gap-2">
              <FaGift className={`text-base ${isFree ? "text-green-500" : "text-gray-400"}`} />
              <span className="text-sm font-medium text-gray-700">Free Membership</span>
              {isFree && (
                <span className="text-xs bg-green-100 text-green-700 font-semibold px-2 py-0.5 rounded-full">
                  ✓ FREE
                </span>
              )}
            </div>
          </div>

          {isFree ? (
            <div className="flex flex-col items-center justify-center py-6 bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl border-2 border-dashed border-green-300">
              <span className="text-5xl font-extrabold bg-gradient-to-r from-green-500 to-emerald-500 bg-clip-text text-transparent mb-2">
                FREE
              </span>
              <p className="text-sm text-gray-500 text-center">
                Members can join without paying any fee
              </p>
            </div>
          ) : (
            <div className="relative">
              <input
                type="number"
                name="saleSettings.price"
                value={formik.values.saleSettings?.price ?? ""}
                onChange={(e) => {
                  const value = e.target.value;
                  if (value === "" || value === "-") {
                    formik.setFieldValue("saleSettings.price", 0);
                  } else {
                    const parsed = parseFloat(value);
                    formik.setFieldValue("saleSettings.price", isNaN(parsed) ? value : parsed);
                  }
                }}
                onBlur={(e) => {
                  const parsed = parseFloat(e.target.value);
                  formik.setFieldValue("saleSettings.price", isNaN(parsed) ? 0 : parsed);
                }}
                placeholder="0.00"
                step="any"
                min="0"
                disabled={isPublished}
                className={`w-full pl-4 pr-36 py-3 text-lg border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary focus:border-transparent ${isPublished ? "bg-gray-50 opacity-60 cursor-not-allowed" : ""}`}
              />

              {/* Custom currency picker with icons */}
              <div className="absolute right-2 top-1/2 -translate-y-1/2" ref={currencyRef}>
                <button
                  type="button"
                  onClick={() => !isPublished && setCurrencyOpen((o) => !o)}
                  disabled={isPublished}
                  className={`flex items-center gap-1.5 bg-gray-100 px-3 py-2 rounded-lg text-sm font-medium border border-gray-300 transition-colors min-w-[90px] justify-between ${isPublished ? "cursor-not-allowed opacity-60" : "hover:bg-gray-200 cursor-pointer"}`}
                >
                  <span className="flex items-center gap-1.5">
                    {CurrencyIcon && (
                      <img
                        src={CurrencyIcon}
                        alt={selectedCurrency}
                        className="w-4 h-4 rounded-full flex-shrink-0"
                        onError={(e) => { e.target.style.display = "none"; }}
                      />
                    )}
                    <span>{selectedCurrency}</span>
                  </span>
                  <BsChevronDown className={`text-xs text-gray-500 transition-transform ${currencyOpen ? "rotate-180" : ""}`} />
                </button>

                {currencyOpen && (
                  <div className="absolute right-0 top-full mt-1 bg-white border border-gray-200 rounded-xl shadow-lg z-20 min-w-[130px] py-1 max-h-52 overflow-y-auto">
                    {availableCurrencies.length === 0 && (
                      <p className="px-3 py-2 text-xs text-gray-400">No tokens on this chain</p>
                    )}
                    {availableCurrencies.map((cur) => {
                      const Icon = currencyIcons[cur];
                      return (
                        <button
                          key={cur}
                          type="button"
                          onClick={() => {
                            formik.setFieldValue("saleSettings.currency", cur);
                            setCurrencyOpen(false);
                          }}
                          className={`w-full flex items-center gap-2.5 px-3 py-2 text-sm hover:bg-gray-50 transition-colors ${
                            cur === selectedCurrency ? "bg-purple-50 text-primary font-semibold" : "text-gray-700"
                          }`}
                        >
                          {Icon && (
                            <img
                              src={Icon}
                              alt={cur}
                              className="w-5 h-5 rounded-full flex-shrink-0"
                              onError={(e) => { e.target.style.display = "none"; }}
                            />
                          )}
                          <span>{cur}</span>
                          {cur === selectedCurrency && (
                            <svg className="w-3.5 h-3.5 text-primary ml-auto" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                          )}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          )}

          {formik.touched.saleSettings?.price && formik.errors.saleSettings?.price && (
            <p className="mt-2 text-sm text-red-500">{formik.errors.saleSettings.price}</p>
          )}

          {/* Revenue Estimate */}
          {!isFree && formik.values.saleSettings?.price > 0 && formik.values.totalSupply > 0 && (
            <div className="mt-4 p-4 bg-green-50 rounded-lg border border-green-200">
              <p className="text-sm text-gray-700">
                <span className="font-semibold">Potential Revenue:</span>{" "}
                <span className="text-green-700 font-bold">
                  {(parseFloat(formik.values.saleSettings.price || 0) * formik.values.totalSupply).toFixed(4)}{" "}
                  {selectedCurrency}
                </span>
              </p>
              <p className="text-xs text-gray-500 mt-1">
                If all {formik.values.totalSupply} NFTs are sold
              </p>
            </div>
          )}

          {/* Free premint notice */}
          {isFree && formik.values.preMintMode && (
            <div className="mt-4 p-4 bg-emerald-50 rounded-lg border border-emerald-200 flex gap-2">
              <span className="text-emerald-600 text-base mt-0.5 flex-shrink-0">✓</span>
              <div className="text-sm text-emerald-800">
                <p className="font-medium mb-0.5">Free membership (Pre-Mint mode)</p>
                <p className="text-xs text-emerald-700">
                  Only users who have created a FunDeSci profile can claim this free NFT, and each account can only claim one.
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Membership Duration */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-3">
          ⏰ Membership Duration
        </label>
        <div className="flex flex-wrap gap-3">
          {membershipDurations.map((dur) => (
            <button
              key={dur.value}
              type="button"
              onClick={() => formik.setFieldValue("membershipDuration", dur.value)}
              className={`px-5 py-2.5 rounded-xl text-sm font-medium transition-all flex items-center gap-2 ${
                formik.values.membershipDuration === dur.value
                  ? "bg-primary text-white"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              }`}
            >
              {dur.label}
              {dur.recommended && (
                <span className="text-xs bg-white/20 px-1.5 py-0.5 rounded">
                  ✓ Recommended
                </span>
              )}
            </button>
          ))}
        </div>
        <p className="text-xs text-gray-500 mt-2 flex items-center gap-1">
          <BsInfoCircle />
          Lifetime memberships create stronger community loyalty
        </p>
      </div>

      {/* Info Box */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
        <h4 className="font-semibold text-blue-900 mb-2 flex items-center gap-2">
          <BsInfoCircle className="text-blue-600" />
          How Payments Work
        </h4>
        <ul className="text-sm text-blue-800 space-y-1 ml-6 list-disc">
          <li>Members mint their own NFT when joining</li>
          <li>Payment goes directly to your wallet (creator)</li>
          <li>Members also pay gas fees for minting</li>
          <li>No intermediaries — you receive 100% of the membership price</li>
        </ul>
      </div>
    </div>
  );
};

export default Step3SaleSettings;
